import React, { useState, useCallback, useEffect } from "react";
import { ethers } from "ethers";
import { useAuth } from "../contexts/AppKitAuthContext";
import { getStoredBetPreference, storeBetPreference } from "../utils/betPreferences";
import { GameUtilities } from "../types";
import { appKitBlockchainService } from "../services/appkitBlockchain";
import { useGameConfig } from "../hooks/useGameConfig";
import InsufficientBalancePopup from "./InsufficientBalancePopup";
import styles from "../styles/GameCreationPopup.module.css";

interface GameCreationPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onCreateGame: (betAmount: string, utilities: GameUtilities) => Promise<void>;
  loading: boolean;
}

const GameCreationPopup: React.FC<GameCreationPopupProps> = ({
  isVisible,
  onClose,
  onCreateGame,
  loading,
}) => {
  const { walletState, updateBalance } = useAuth();
  const [selectedBetAmount, setSelectedBetAmount] = useState(() => getStoredBetPreference());
  const [utilities, setUtilities] = useState<GameUtilities>({});
  const [boostCount, setBoostCount] = useState(1);
  const [dxfAllowance, setDxfAllowance] = useState("0");
  const [checkingAllowance, setCheckingAllowance] = useState(false);
  const [approvingDxf, setApprovingDxf] = useState(false);
  const [showInsufficientBalancePopup, setShowInsufficientBalancePopup] = useState(false);

  const {
    loading: configLoading,
    getBetAmountOptions,
    isRowSkipEnabled,
    isDXFMiningBoostEnabled,
    isPhoenixRevivalEnabled,
    getRowSkipPrice,
    getDXFMiningBoostPrice,
    getPhoenixRevivalPrice,
    getDXFMiningBoostMaxCount,
    getRowSkipDescription,
    getDXFMiningBoostDescription,
    getPhoenixRevivalDescription,
  } = useGameConfig();
  const betOptions = getBetAmountOptions();

  const handleRowSkipToggle = useCallback(() => {
    setUtilities(prev => ({
      ...prev,
      rowSkip: prev.rowSkip?.purchased 
        ? undefined 
        : { purchased: true }
    }));
  }, []);

  const handleDxfMiningBoostToggle = useCallback(() => {
    setUtilities(prev => ({
      ...prev,
      dxfMiningBoost: prev.dxfMiningBoost?.purchased
        ? undefined
        : { purchased: true, count: boostCount }
    }));
  }, [boostCount]);

  const handlePhoenixRevivalToggle = useCallback(() => {
    setUtilities(prev => ({
      ...prev,
      phoenixRevival: prev.phoenixRevival?.purchased 
        ? undefined 
        : { purchased: true }
    }));
  }, []);

  const handleBoostCountChange = useCallback((newCount: number) => {
    setBoostCount(Math.max(1, Math.min(10, newCount))); // Limit between 1-10
    if (utilities.dxfMiningBoost?.purchased) {
      setUtilities(prev => ({
        ...prev,
        dxfMiningBoost: { purchased: true, count: newCount }
      }));
    }
  }, [utilities.dxfMiningBoost?.purchased]);

  const calculateTotalDxfCost = useCallback(() => {
    let totalCost = 0;
    
    if (utilities.rowSkip?.purchased) {
      totalCost += getRowSkipPrice(selectedBetAmount);
    }
    
    if (utilities.dxfMiningBoost?.purchased) {
      totalCost += getDXFMiningBoostPrice(selectedBetAmount) * utilities.dxfMiningBoost.count;
    }
    
    if (utilities.phoenixRevival?.purchased) {
      totalCost += getPhoenixRevivalPrice(selectedBetAmount);
    }
    
    return totalCost;
  }, [utilities, selectedBetAmount, getRowSkipPrice, getDXFMiningBoostPrice, getPhoenixRevivalPrice]);

  // Check DXF allowance when popup opens or wallet changes
  const checkDxfAllowance = useCallback(async () => {
    if (!walletState?.address || !isVisible) return;
    
    setCheckingAllowance(true);
    try {
      const allowance = await appKitBlockchainService.getDXFAllowance(walletState.address);
      setDxfAllowance(allowance);
    } catch (error) {
      console.error("Failed to check DXF allowance:", error);
      setDxfAllowance("0");
    } finally {
      setCheckingAllowance(false);
    }
  }, [walletState?.address, isVisible]);


  // Check if user has sufficient DXF balance
  const hasSufficientDxfBalance = useCallback(() => {
    const totalCost = calculateTotalDxfCost();
    if (totalCost === 0) return true;
    
    try {
      const balanceBigInt = ethers.parseUnits(walletState?.dxfBalance || "0", 18);
      const requiredBigInt = ethers.parseUnits(totalCost.toString(), 18);
      return balanceBigInt >= requiredBigInt;
    } catch {
      return false;
    }
  }, [walletState?.dxfBalance, calculateTotalDxfCost]);

  // Check if user has sufficient DXF allowance
  const hasSufficientAllowance = useCallback(() => {
    const totalCost = calculateTotalDxfCost();
    if (totalCost === 0) return true;
    
    try {
      const allowanceBigInt = ethers.parseUnits(dxfAllowance, 18);
      const requiredBigInt = ethers.parseUnits(totalCost.toString(), 18);
      return allowanceBigInt >= requiredBigInt;
    } catch {
      return false;
    }
  }, [dxfAllowance, calculateTotalDxfCost]);

  // Handle DXF approval
  const handleApproveDxf = useCallback(async () => {
    if (!walletState?.address || approvingDxf) return;
    
    const totalCost = calculateTotalDxfCost();
    if (totalCost === 0) return;
    
    setApprovingDxf(true);
    try {
      // Approve max uint256 to avoid repeated approvals
      const maxApproval = ethers.MaxUint256.toString();
      const maxApprovalFormatted = ethers.formatUnits(maxApproval, 18);
      await appKitBlockchainService.approveDXF(maxApprovalFormatted);
      
      // Refresh allowance after approval
      setTimeout(() => {
        checkDxfAllowance();
      }, 2000);
    } catch (error) {
      console.error("Failed to enable utilities:", error);
      // Could add error notification here in the future
    } finally {
      setApprovingDxf(false);
    }
  }, [walletState?.address, calculateTotalDxfCost, checkDxfAllowance, approvingDxf]);

  // Check allowance and refresh balance when popup opens or dependencies change
  useEffect(() => {
    if (isVisible && walletState?.address) {
      checkDxfAllowance();
      // Trigger balance refresh to get latest DXF balance
      updateBalance();
    }
  }, [isVisible, walletState?.address, checkDxfAllowance, updateBalance]);

  const handleCreateGame = useCallback(async () => {
    if (loading) return;
    
    try {
      await onCreateGame(selectedBetAmount, utilities);
      // Reset form after successful creation
      setUtilities({});
      setBoostCount(1);
    } catch (error) {
      // Error handling is done in the parent component
      console.error("Failed to create game:", error);
    }
  }, [selectedBetAmount, utilities, onCreateGame, loading]);

  // Check if user has sufficient BNB for bet
  const betInWei = ethers.parseEther(selectedBetAmount);
  const balanceInWei = ethers.parseEther(walletState?.balance || "0");
  const hasSufficientBnb = BigInt(betInWei.toString()) <= balanceInWei;

  // Calculate total DXF cost
  const totalDxfCost = calculateTotalDxfCost();

  // Check if user has sufficient DXF balance and allowance
  const hasSufficientDxfBalanceAndAllowance = hasSufficientDxfBalance() && hasSufficientAllowance();

  if (!isVisible) return null;

  return (
    <div className={styles.gameCreationOverlay}>
      <div className={styles.gameCreationPopup}>
        <div className={styles.gameCreationHeader}>
          <div className={styles.gameCreationTitle}>START NEW GAME</div>
          <button className={styles.gameCreationClose} onClick={onClose}>
            [X]
          </button>
        </div>

        <div className={styles.gameCreationContent}>
          {/* Bet Amount Selection */}
          <div className={styles.gameCreationSection}>
            <div className={styles.gameCreationSectionTitle}>BET AMOUNT</div>
            <div className={styles.betAmountGrid}>
              {betOptions.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setSelectedBetAmount(amount);
                    storeBetPreference(amount);
                  }}
                  className={`${styles.betAmountOption} ${selectedBetAmount === amount ? styles.selected : ''}`}
                >
                  {amount} BNB
                </button>
              ))}
            </div>
            {!hasSufficientBnb && (
              <div 
                className={`${styles.insufficientBalanceWarning} ${styles.clickable}`} 
                onClick={() => setShowInsufficientBalancePopup(true)}
                style={{ cursor: 'pointer' }}
              >
                Insufficient BNB balance - Click to get BNB
              </div>
            )}
          </div>

          {/* Utilities Selection */}
          <div className={styles.gameCreationSection}>
            <div className={styles.gameCreationSectionTitle}>UTILITIES</div>
            
            {/* Show loading state for utility config */}
            {configLoading && (
              <div className={styles.utilityLoading}>Loading utility configurations...</div>
            )}
            
            {/* Phoenix Revival Utility */}
            {isPhoenixRevivalEnabled() && (
              <div className={styles.utilityOption}>
                <div className={styles.utilityHeader}>
                  <label className={styles.utilityCheckbox}>
                    <input
                      type="checkbox"
                      checked={utilities.phoenixRevival?.purchased || false}
                      onChange={handlePhoenixRevivalToggle}
                    />
                    <span className={styles.utilityCheckmark}></span>
                    <span className={styles.utilityName}>Phoenix Revival</span>
                    <span className={styles.utilityCost}>{getPhoenixRevivalPrice(selectedBetAmount)} DXF</span>
                  </label>
                </div>
                <div className={styles.utilityDescription}>
                  {getPhoenixRevivalDescription()}
                </div>
              </div>
            )}

            {/* Row Skip Utility */}
            {isRowSkipEnabled() && (
              <div className={styles.utilityOption}>
                <div className={styles.utilityHeader}>
                  <label className={styles.utilityCheckbox}>
                    <input
                      type="checkbox"
                      checked={utilities.rowSkip?.purchased || false}
                      onChange={handleRowSkipToggle}
                    />
                    <span className={styles.utilityCheckmark}></span>
                    <span className={styles.utilityName}>Row Skip</span>
                    <span className={styles.utilityCost}>{getRowSkipPrice(selectedBetAmount)} DXF</span>
                  </label>
                </div>
                <div className={styles.utilityDescription}>
                  {getRowSkipDescription()}
                </div>
              </div>
            )}

            {/* DXF Mining Boost Utility */}
            {isDXFMiningBoostEnabled() && (
              <div className={styles.utilityOption}>
                <div className={styles.utilityHeader}>
                  <label className={styles.utilityCheckbox}>
                    <input
                      type="checkbox"
                      checked={utilities.dxfMiningBoost?.purchased || false}
                      onChange={handleDxfMiningBoostToggle}
                    />
                    <span className={styles.utilityCheckmark}></span>
                    <span className={styles.utilityName}>DXF Mining Boost</span>
                    <span className={styles.utilityCost}>{getDXFMiningBoostPrice(selectedBetAmount)} DXF each</span>
                  </label>
                </div>
                <div className={styles.utilityDescription}>
                  {getDXFMiningBoostDescription()}
                </div>
              {utilities.dxfMiningBoost?.purchased && (
                <div className={styles.boostCountControls}>
                  <span className={styles.boostCountLabel}>Boost Count:</span>
                  <div className={styles.boostCountInput}>
                    <button 
                      onClick={() => handleBoostCountChange(boostCount - 1)}
                      disabled={boostCount <= 1}
                      className={styles.boostCountBtn}
                    >
                      -
                    </button>
                    <span className={styles.boostCountValue}>{boostCount}</span>
                    <button 
                      onClick={() => handleBoostCountChange(boostCount + 1)}
                      disabled={boostCount >= getDXFMiningBoostMaxCount()}
                      className={styles.boostCountBtn}
                    >
                      +
                    </button>
                  </div>
                  <span className={styles.boostEffect}>+{boostCount * 10}% DXF rewards</span>
                </div>
              )}
              </div>
            )}
          </div>

          {/* Cost Summary */}
          {totalDxfCost > 0 && (
            <div className={styles.costSummary}>
              <div className={styles.costSummaryTitle}>UTILITY COSTS</div>
              <div className={styles.costSummaryItems}>
                {utilities.phoenixRevival?.purchased && (
                  <div className={styles.costItem}>
                    <span>Phoenix Revival</span>
                    <span>{getPhoenixRevivalPrice(selectedBetAmount)} DXF</span>
                  </div>
                )}
                {utilities.rowSkip?.purchased && (
                  <div className={styles.costItem}>
                    <span>Row Skip</span>
                    <span>{getRowSkipPrice(selectedBetAmount)} DXF</span>
                  </div>
                )}
                {utilities.dxfMiningBoost?.purchased && (
                  <div className={styles.costItem}>
                    <span>DXF Mining Boost x{utilities.dxfMiningBoost.count}</span>
                    <span>{getDXFMiningBoostPrice(selectedBetAmount) * utilities.dxfMiningBoost.count} DXF</span>
                  </div>
                )}
              </div>
              <div className={styles.costTotal}>
                <span>Total Cost:</span>
                <span>{totalDxfCost} DXF</span>
              </div>
            </div>
          )}

          {/* DXF Balance & Allowance Section */}
          {totalDxfCost > 0 && !hasSufficientDxfBalanceAndAllowance && (
            <div className={styles.dxfAllowanceSection}>
              <div className={styles.dxfAllowanceInfo}>
                <div className={styles.dxfAllowanceItem}>
                  <span className={styles.dxfAllowanceLabel}>Balance:</span>
                  <span className={styles.dxfAllowanceValue}>
                    {parseFloat(walletState?.dxfBalance || "0").toFixed(0)} DXF
                  </span>
                </div>
                {/* <div className={styles.dxfAllowanceItem}>
                  <span className={styles.dxfAllowanceLabel}>Allowance:</span>
                  <span className={styles.dxfAllowanceValue}>
                    {checkingAllowance ? "Checking..." : `${parseFloat(dxfAllowance).toFixed(0)} DXF`}
                  </span>
                </div> */}
                <div className={styles.dxfAllowanceItem}>
                  <span className={styles.dxfAllowanceLabel}>Required:</span>
                  <span className={styles.dxfAllowanceValue}>{totalDxfCost} DXF</span>
                </div>
              </div>
              
              {!hasSufficientDxfBalance() && (
                <div className={styles.insufficientDxfWarning}>
                  Insufficient DXF balance
                </div>
              )}
              
              {hasSufficientDxfBalance() && !hasSufficientAllowance() && (
                <div className={styles.dxfApprovalSection}>
                  <div className={styles.approvalExplanation}>
                    One-time setup to use DXF for game features
                  </div>
                  <button
                    onClick={handleApproveDxf}
                    disabled={approvingDxf || checkingAllowance}
                    className={styles.dxfApprovalBtn}
                  >
                    {approvingDxf ? "Enabling utilities..." : "Enable Utilities"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Create Game Button */}
          <button
            onClick={handleCreateGame}
            disabled={loading || !hasSufficientBnb || (totalDxfCost > 0 && !hasSufficientDxfBalanceAndAllowance)}
            className={`${styles.gameCreationCreateBtn} ${(!hasSufficientBnb || (totalDxfCost > 0 && (!hasSufficientDxfBalance() || !hasSufficientAllowance()))) ? styles.disabled : ''}`}
          >
            {loading ? "Creating Game..." : "Create Game"}
          </button>
        </div>
      </div>

      <InsufficientBalancePopup
        isOpen={showInsufficientBalancePopup}
        onClose={() => setShowInsufficientBalancePopup(false)}
        requiredAmount={selectedBetAmount}
      />
    </div>
  );
};

export default GameCreationPopup;